import { convertFromHTML, convertToHTML } from "draft-convert";
import draftJs from "draft-js";
import handlebars from "handlebars";
import handlebarsHelpers from "just-handlebars-helpers";
const { ContentState, convertFromRaw, convertToRaw, Modifier, SelectionState } = draftJs;
handlebarsHelpers.registerHelpers(handlebars);
// Source: https://github.com/sindresorhus/escape-string-regexp/blob/main/index.js
const prepareForRegExp = (string) => {
    // Escape characters with special meaning either inside or outside character sets.
    // Use a simple backslash escape when it’s always valid, and a `\xnn` escape when the simpler form would be disallowed by Unicode patterns’ stricter grammar.
    return string.replace(/[|\\{}()[\]^$+*?.]/g, "\\$&").replace(/-/g, "\\x2d");
};
export const replaceInText = (text = "", replacements = {}) => {
    if (!text)
        return text;
    return Object.keys(replacements).reduce((text, pattern) => {
        const replacement = replacements[pattern];
        if (replacement) {
            return text.replace(new RegExp(prepareForRegExp(pattern), "ig"), replacement);
        }
        return text;
    }, text);
};
// Source: https://github.com/facebook/draft-js/issues/2157#issuecomment-519840458
// Substitutes literal `key` occurrences in each block with `value`, using
// Draft.js's `Modifier.replaceText` so inline style / entity offsets stay
// aligned. Substitutes empty strings too — a token whose Handlebars-resolved
// value is "" must visibly resolve to "" in the output rather than leaving the
// literal `{{token}}` behind (Decision 4 in the change spec).
function replaceTextInDraftJSRawContentSingleBlock(rawContentState, replacements) {
    let contentState = convertFromRaw(rawContentState);
    let blockMap = contentState.getBlockMap().toArray();
    let start;
    Object.entries(replacements).forEach(([key, value]) => {
        if (value === undefined)
            return;
        if (value.includes(key))
            return; // prevent from infinite cycle
        for (let blockIndex in blockMap) {
            do {
                const block = blockMap[blockIndex];
                const blockKey = block?.getKey();
                const text = block?.getText() ?? "";
                start = text.indexOf(key);
                if (blockKey && start > -1) {
                    contentState = Modifier.replaceText(contentState, SelectionState.createEmpty(blockKey).merge({
                        anchorOffset: start,
                        focusOffset: start + key.length,
                    }), value);
                    blockMap = contentState.getBlockMap().toArray();
                }
            } while (start > -1);
        }
    });
    return convertToRaw(contentState);
}
// Case-sensitive literal substitution that mirrors the Draft.js block path
// (substitutes "" too). Used for the plain-text fallback branch of
// `getRawDraftStateWithReplacements`. NOT `replaceInText` — that helper is
// case-insensitive and treats empty as "skip", which would diverge from
// Handlebars semantics here.
function applyHandlebarsReplacements(text, replacements) {
    let out = text;
    for (const [key, value] of Object.entries(replacements)) {
        if (value === undefined)
            continue;
        if (value.includes(key))
            continue;
        out = out.split(key).join(value);
    }
    return out;
}
export const getRawDraftStateWithReplacements = (blocks, text, textReplacements) => {
    let rawDraftContentState = null;
    if (blocks) {
        try {
            rawDraftContentState = replaceTextInDraftJSRawContentSingleBlock(JSON.parse(blocks), textReplacements ?? {});
            // eslint-disable-next-line no-empty
        }
        catch (e) { }
    }
    if (!rawDraftContentState) {
        rawDraftContentState = convertToRaw(ContentState.createFromText(applyHandlebarsReplacements(text ?? "", textReplacements ?? {})));
    }
    return rawDraftContentState;
};
export function textToStringifiedRawContentState(text) {
    return JSON.stringify(convertToRaw(ContentState.createFromText(text)));
}
export const htmlToDraftState = (html) => {
    return convertFromHTML({
        htmlToEntity: (nodeName, node, createEntity) => {
            if (nodeName === "a") {
                return createEntity("LINK", "MUTABLE", { url: node.href });
            }
        },
        htmlToBlock: (nodeName, node) => {
            if (nodeName === "blockquote") {
                return {
                    type: "blockquote",
                    data: {},
                };
            }
        },
    })(html);
};
export const draftStateToHtml = (contentState) => {
    return convertToHTML({
        entityToHTML: (entity, originalText) => {
            if (entity.type === "LINK") {
                return `<a href="${entity.data.url}">${originalText}</a>`;
            }
            return originalText;
        },
    })(contentState);
};
//# sourceMappingURL=functions.js.map